import { useState, type FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderQueryKey, type Order, loadOrder } from '@/api/registration';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { useTenantApi } from '@/account/hook/useTenantApi';
import ConfirmationPage from './ConfirmationPage';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';

const PAID = 'Paid';
const APPROVED = 'Approved';
const WAITING_FOR_PAYMENT = 'Waiting for Payment';

const Confirmation: FC = () => {
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { eventId } = useRegisterRoute();
  const { data: eventSetting } = useEventSettingsQuery(eventId);
  const [searchParams] = useSearchParams();
  const orderHashParam = searchParams.get('orderHash');
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const [orderHash, setOrderHash] = useState<string | null>(null);

  const { data, isLoading, error: fetchError } = useQuery<ApiResponseSingle<Order>, Error>({
    queryKey: [orderQueryKey, { orderHash }],
    queryFn: async () => {
      if (!orderHash) {
        throw new Error('orderHash is missing, cannot complete query');
      }
      return await loadOrder(createTenantApiRequest)(orderHash);
    },
    enabled: !!orderHash && !isApiPreparing
  });

  useEffect(() => {
    const _orderHash = orderHashParam ?? groupRegistrationQuery?.data?.orderHash;
    if (_orderHash) {
      setOrderHash(_orderHash);
    }
  }, [groupRegistrationQuery?.data?.orderHash, orderHashParam]);

  const isInvoice = data?.item?.paymentOption === 'invoice';
  const statusesToCheck = isInvoice ? [WAITING_FOR_PAYMENT, APPROVED, PAID] : [APPROVED, PAID];

  return (
    <ConfirmationPage data={data} isLoading={isLoading} fetchError={fetchError} statusToCheck={statusesToCheck} confirmationMetadata={eventSetting?.pageMetadata?.confirmation} />
  );
};

export default Confirmation;
